/**
 * Posting store — Task 8
 * Mirrors FOM posting.html D object exactly.
 *
 * DUAL-TAB MODEL (Next Shift Draft parity with FOM):
 *   - `data`      → current shift (localStorage: fom_daily_posting)
 *   - `nextData`  → next shift draft (localStorage: fom_daily_posting_next)
 *   - `activeTab` → which one mutation actions operate on ('current' | 'next')
 *
 * All mutation actions (setDate, addPost, updateBreak, etc.) route through
 * `update()` which dispatches to the active tab. This means PersonCard and
 * PostingLayout can stay tab-agnostic — they just render `activeData()`.
 */
import { create } from 'zustand'
import { workerGetV1 } from '../api/worker'
import { markDirty } from './sync'

export interface PostEntry {
  post: string
  from: string
  to: string
}

export interface SecondaryTask {
  taskId: string
  time: string
}

export interface Break {
  time: string
  endTime: string
  coveredBy: string
}

export interface PersonEntry {
  cs: string
  name: string
  time: string
  isOIC: boolean
  posts: PostEntry[]
  tasks: SecondaryTask[]
  brk: Break
}

export interface PostingData {
  date: string
  shift: string
  supervisors: PersonEntry[]
  officers: PersonEntry[]
  notes: string
  weather: { temp: string; conditions: string }
}

export type PostingTab = 'current' | 'next'

// Shift time defaults — exact from FOM source
export function supShiftTime(shift: string) {
  return shift === 'Morning' ? '0645-1445'
    : shift === 'Evening' ? '1445-2245'
    : shift === 'Night' ? '2245-0645' : ''
}

export function ofcShiftTime(shift: string) {
  return shift === 'Morning' ? '0700-1500'
    : shift === 'Evening' ? '1500-2300'
    : shift === 'Night' ? '2300-0700' : ''
}

// Next-shift cascade: Morning → Evening → Night → Morning
export function nextShiftLabel(current: string): string {
  return current === 'Morning' ? 'Evening'
    : current === 'Evening' ? 'Night'
    : 'Morning'
}

// Night rolls over to the following calendar day's Morning
export function nextShiftDate(curDate: string, curShift: string): string {
  if (curShift !== 'Night') return curDate
  try {
    const d = new Date(curDate + 'T12:00:00')
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  } catch { return curDate }
}

/**
 * Auto-calculate break end time: start time + 30 min.
 * Matches FOM posting.html calcBreakEnd() exactly.
 * Input/output format: 4-digit HHMM (e.g. "1200" → "1230").
 * Handles 24h rollover.
 */
export function calcBreakEnd(startTime: string): string {
  if (!startTime || startTime.length < 3) return ''
  let s = startTime.replace(':', '')
  while (s.length < 4) s = '0' + s
  let h = parseInt(s.substring(0, 2))
  let m = parseInt(s.substring(2, 4))
  if (isNaN(h) || isNaN(m)) return ''
  m += 30
  if (m >= 60) { h += 1; m -= 60 }
  if (h >= 24) h -= 24
  return String(h).padStart(2, '0') + String(m).padStart(2, '0')
}

export const SECONDARY_TASKS = [
  { id: 'fe_upper',   label: 'FE Upper Level' },
  { id: 'fe_lower',   label: 'FE Lower Level' },
  { id: 'detex',      label: 'Detex' },
  { id: 'mall_doors', label: 'Mall Doors' },
  { id: 'esc_tv',     label: 'Esc/TV' },
  { id: 'svc_doors',  label: 'Svc Doors' },
  { id: 'j_ext',      label: 'J Ext' },
  { id: 'cny',        label: 'Lunar New Year' },
]

/**
 * Auto-fill time for a secondary task based on the task + shift + day of week.
 * Returns a 4-digit HHMM string, or '' when there's no rule (Night, unmatched,
 * or tasks like Lunar New Year). The time field stays editable after — this
 * only sets an initial value when the task is first picked.
 *
 * Rules (ET day of week):
 *   Morning (07:00–15:00):
 *     mall_doors, fe_upper, fe_lower, detex → 0700
 *     esc_tv (one combined task)            → 0930
 *   Evening (15:00–23:00):
 *     fe_upper, fe_lower → 1500 (all days)
 *     mall_doors, esc_tv → 2000 Mon–Thu / 2100 Fri–Sat / 1800 Sun
 *     svc_doors, j_ext   → 2100 Mon–Thu / 2200 Fri–Sat / 1900 Sun
 *   Night: blank for now.
 */
export function secondaryTaskTime(taskId: string, shift: string): string {
  if (!taskId) return ''
  // Day of week in America/New_York: 0=Sun, 1=Mon ... 6=Sat
  let dow = new Date().getDay()
  try {
    const etDay = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short' })
    const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
    if (etDay in map) dow = map[etDay as keyof typeof map]
  } catch { /* fall back to local getDay */ }
  const isFriSat = dow === 5 || dow === 6
  const isSun = dow === 0

  if (shift === 'Morning') {
    if (['mall_doors', 'fe_upper', 'fe_lower', 'detex'].includes(taskId)) return '0700'
    if (taskId === 'esc_tv') return '0930'
    return ''
  }
  if (shift === 'Evening') {
    if (taskId === 'fe_upper' || taskId === 'fe_lower') return '1500'
    if (taskId === 'mall_doors' || taskId === 'esc_tv') return isFriSat ? '2100' : isSun ? '1800' : '2000'
    if (taskId === 'svc_doors' || taskId === 'j_ext') return isFriSat ? '2200' : isSun ? '1900' : '2100'
    return ''
  }
  // Night and anything else: blank for now
  return ''
}

export const PRIMARY_POSTS = ['Vehicle', 'Mall', 'Desk', 'Special']

function makeEntry(): PersonEntry {
  return { cs: '', name: '', time: '', isOIC: false, posts: [], tasks: [], brk: { time: '', endTime: '', coveredBy: '' } }
}

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : fallback
  } catch { return fallback }
}

/**
 * Normalize a PostingData blob into a safe shape.
 *
 * Cloud sync can write `'{}'` into the `fom_daily_posting` localStorage key
 * because the D1 `shift_data.fom_daily_posting` column defaults to `'{}'`.
 * Parsing that produces an empty object with no `supervisors`/`officers`
 * arrays, which crashes `active.supervisors.map(...)` at render time.
 */
function normalizeData(raw: Partial<PostingData> | null | undefined, defaults: PostingData): PostingData {
  if (!raw || typeof raw !== 'object') return { ...defaults }
  const shift = typeof raw.shift === 'string' && raw.shift ? raw.shift : defaults.shift
  const supervisors = Array.isArray(raw.supervisors) ? raw.supervisors : []
  const officers    = Array.isArray(raw.officers)    ? raw.officers    : []
  // Pad the current-shift form up to 1 supervisor + 3 officers so the page
  // always has visible rows to fill in. Skipped for an explicitly empty
  // `defaults` (the next-shift draft's emptyNext()), which stays blank
  // until the user actively switches to that tab.
  const wantDefaults = defaults.supervisors.length > 0 || defaults.officers.length > 0
  const paddedSups = wantDefaults && supervisors.length === 0
    ? [{ ...makeEntry(), time: supShiftTime(shift) }]
    : supervisors
  const paddedOfcs = wantDefaults
    ? [...officers]
    : officers
  if (wantDefaults) {
    while (paddedOfcs.length < 3) paddedOfcs.push({ ...makeEntry(), time: ofcShiftTime(shift) })
  }
  return {
    date:        typeof raw.date === 'string' && raw.date ? raw.date : defaults.date,
    shift,
    supervisors: paddedSups,
    officers:    paddedOfcs,
    notes:       typeof raw.notes === 'string' ? raw.notes : '',
    weather:     raw.weather && typeof raw.weather === 'object'
                   ? { temp: raw.weather.temp ?? '', conditions: raw.weather.conditions ?? '' }
                   : { temp: '', conditions: '' },
  }
}

function saveCurrent(data: PostingData) {
  try { localStorage.setItem('fom_daily_posting', JSON.stringify(data)) } catch { /* silent */ }
  try { window.dispatchEvent(new CustomEvent('castellan:posting-changed')) } catch { /* silent */ }
  markDirty()
}

function saveNext(data: PostingData) {
  try { localStorage.setItem('fom_daily_posting_next', JSON.stringify(data)) } catch { /* silent */ }
  markDirty()
}

// Load roster for name lookup
const NON_POSTING_CATS = ['Engineering', 'Management', 'FCPD', 'Dispatchers', 'Other']

export function loadRoster(): { callSign: string; name: string }[] {
  try {
    const raw = localStorage.getItem('fom_roster')
    if (!raw) return []
    const roster = JSON.parse(raw) as Record<string, { name: string; callSign?: string }[]>
    return Object.entries(roster)
      .filter(([cat]) => !NON_POSTING_CATS.includes(cat))
      .flatMap(([, entries]) => entries)
      .map(r => ({ callSign: r.callSign ?? '', name: r.name ?? '' }))
      .filter(r => r.callSign)
  } catch { return [] }
}

function getToday() {
  try {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
  } catch {
    return new Date().toISOString().slice(0, 10)
  }
}

function detectShift(): string {
  const m = etMinutes()
  // Morning: 07:00 (420) – 15:00 (900)
  // Evening: 15:00 (900) – 23:00 (1380)
  // Night:   23:00 (1380) – 07:00 (420 next day)
  if (m >= 420 && m < 900)  return 'Morning'
  if (m >= 900 && m < 1380) return 'Evening'
  return 'Night'
}

function blankData(): PostingData {
  const shift = detectShift()
  return {
    date: getToday(),
    shift,
    supervisors: [{ ...makeEntry(), time: supShiftTime(shift) }],
    officers: [
      { ...makeEntry(), time: ofcShiftTime(shift) },
      { ...makeEntry(), time: ofcShiftTime(shift) },
      { ...makeEntry(), time: ofcShiftTime(shift) },
    ],
    notes: '',
    weather: { temp: '', conditions: '' },
  }
}

function emptyNext(): PostingData {
  return { date: '', shift: '', supervisors: [], officers: [], notes: '', weather: { temp: '', conditions: '' } }
}

// Minutes past midnight in America/New_York (for shift-window checks)
function etMinutes(): number {
  try {
    const s = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit' })
    const [hh, mm] = s.split(':').map(n => parseInt(n))
    return hh * 60 + mm
  } catch {
    const d = new Date()
    return d.getHours() * 60 + d.getMinutes()
  }
}

interface PostingState {
  data: PostingData
  nextData: PostingData
  activeTab: PostingTab
  /** Whether the Next Shift tab button should be visible (within window OR draft exists). */
  nextTabVisible: boolean

  activeData: () => PostingData
  setActiveTab: (tab: PostingTab) => void
  switchTab: (tab: PostingTab) => void
  promoteNextShift: () => void
  checkNextShiftWindow: () => void

  setDate: (d: string) => void
  setShift: (s: string) => void
  setNotes: (n: string) => void
  setWeather: (w: { temp: string; conditions: string }) => void
  addSupervisor: () => void
  addOfficer: () => void
  updateSup: (i: number, patch: Partial<PersonEntry>) => void
  updateOfc: (i: number, patch: Partial<PersonEntry>) => void
  removeSup: (i: number) => void
  removeOfc: (i: number) => void
  addPost: (type: 'sup' | 'ofc', i: number) => void
  removePost: (type: 'sup' | 'ofc', i: number, pi: number) => void
  updatePost: (type: 'sup' | 'ofc', i: number, pi: number, field: keyof PostEntry, val: string) => void
  addTask: (type: 'sup' | 'ofc', i: number) => void
  removeTask: (type: 'sup' | 'ofc', i: number, ti: number) => void
  updateTask: (type: 'sup' | 'ofc', i: number, ti: number, field: keyof SecondaryTask, val: string) => void
  updateBreak: (type: 'sup' | 'ofc', i: number, field: keyof Break, val: string) => void
  pickCS: (type: 'sup' | 'ofc', i: number, cs: string, shift: string) => void
  hydrateFromStorage: () => void
  clearForm: () => void
}

export const usePostingStore = create<PostingState>((set, get) => {
  const initialRaw = load<Partial<PostingData> | null>('fom_daily_posting', null)
  let initial = normalizeData(initialRaw, blankData())
  // Auto-correct shift on first load if date is today
  const todayInit = getToday()
  if (initial.date === todayInit) {
    const detected = detectShift()
    if (initial.shift !== detected) {
      initial = {
        ...initial,
        shift: detected,
        supervisors: initial.supervisors.map(s => ({ ...s, time: supShiftTime(detected) })),
        officers:    initial.officers.map(o => ({ ...o, time: ofcShiftTime(detected) })),
      }
      saveCurrent(initial)
    }
  }

  const initialNextRaw = load<Partial<PostingData> | null>('fom_daily_posting_next', null)
  const initialNext: PostingData = initialNextRaw
    ? normalizeData(initialNextRaw, emptyNext())
    : emptyNext()

  // Route a mutator through whichever tab is active
  function update(fn: (d: PostingData) => PostingData) {
    localStorage.setItem('fom_posting_last_edit', String(Date.now()))
    const state = get()
    if (state.activeTab === 'next') {
      const next = fn(state.nextData)
      saveNext(next)
      set({ nextData: next })
    } else {
      const next = fn(state.data)
      saveCurrent(next)
      set({ data: next })
    }
  }

  function arr(type: 'sup' | 'ofc', d: PostingData) {
    return type === 'sup' ? d.supervisors : d.officers
  }

  function setArr(type: 'sup' | 'ofc', d: PostingData, entries: PersonEntry[]): PostingData {
    return type === 'sup' ? { ...d, supervisors: entries } : { ...d, officers: entries }
  }

  return {
    data: initial,
    nextData: initialNext,
    activeTab: 'current',
    nextTabVisible: !!initialNextRaw,

    activeData: () => {
      const s = get()
      return s.activeTab === 'next' ? s.nextData : s.data
    },

    setActiveTab: (tab) => set({ activeTab: tab }),

    switchTab: (tab) => {
      const state = get()
      if (tab === 'next') {
        // Try to fetch carry-over data from cloud first
        const shift = nextShiftLabel(state.data.shift || 'Morning')
        const date  = nextShiftDate(state.data.date || getToday(), state.data.shift || 'Morning')
        workerGetV1<{ shift: string; date: string; posting: any }>('/api/v1/shift/next-posting')
          .then(res => {
            if (res.posting && (res.posting.supervisors?.length > 0 || res.posting.officers?.length > 0)) {
              // Cloud has carry-over data — use it, merging with any local draft
              const cloudPosting = {
                ...res.posting,
                shift: res.shift,
                date: res.date,
              }
              saveNext(cloudPosting)
              set({ activeTab: 'next', nextData: cloudPosting })
            } else {
              // No carry-over — seed with blank entries as before
              let nextData = state.nextData
              if (!nextData.shift || !nextData.date || (nextData.supervisors.length === 0 && nextData.officers.length === 0)) {
                const sups = [{ ...makeEntry(), time: supShiftTime(shift) }]
                const ofcs = [1,2,3].map(() => ({ ...makeEntry(), time: ofcShiftTime(shift) }))
                nextData = { ...nextData, shift, date, supervisors: sups, officers: ofcs }
                saveNext(nextData)
              }
              set({ activeTab: 'next', nextData })
            }
          })
          .catch(() => {
            // Fallback to local draft on network error
            let nextData = state.nextData
            if (!nextData.shift || !nextData.date || (nextData.supervisors.length === 0 && nextData.officers.length === 0)) {
              const sups = [{ ...makeEntry(), time: supShiftTime(shift) }]
              const ofcs = [1,2,3].map(() => ({ ...makeEntry(), time: ofcShiftTime(shift) }))
              nextData = { ...nextData, shift, date, supervisors: sups, officers: ofcs }
              saveNext(nextData)
            }
            set({ activeTab: 'next', nextData })
          })
        // Set tab immediately, data will update when fetch resolves
        set({ activeTab: 'next' })
      } else {
        set({ activeTab: 'current' })
      }
    },

    promoteNextShift: () => {
      const state = get()
      // Copy draft → current shift, clear draft, return to current tab
      const promoted = { ...state.nextData }
      saveCurrent(promoted)
      // Clear merge hash so mergePostingToPersonnel runs immediately
      // and Personnel on Duty updates from the promoted data
      try { localStorage.removeItem('fom_last_posting_merge') } catch { /* silent */ }
      // Write promoted data to the next_draft key too so D1 push picks it up
      try { localStorage.removeItem('fom_daily_posting_next') } catch { /* silent */ }
      set({
        data: promoted,
        nextData: emptyNext(),
        activeTab: 'current',
        nextTabVisible: false,
      })
      // Dispatch posting-changed so the desktop store re-hydrates immediately
      try { window.dispatchEvent(new CustomEvent('castellan:posting-changed')) } catch { /* silent */ }
      markDirty()
    },

    checkNextShiftWindow: () => {
      // Show Next Shift tab when within 30 mins of current shift end,
      // OR when already on the next tab, OR when a draft is saved.
      const state = get()
      const m = etMinutes()
      const shift = state.data.shift
      // Shift end in minutes: Morning 15:00=900, Evening 23:00=1380, Night 07:00=420
      const shiftEnd = shift === 'Morning' ? 900 : shift === 'Evening' ? 1380 : 420
      const windowStart = shiftEnd - 30
      let inWindow: boolean
      if (shift === 'Night') {
        inWindow = (m >= windowStart && m < shiftEnd) || (m >= 0 && m < 60)
      } else if (shift === 'Evening') {
        inWindow = (m >= windowStart) || (m < 60)
      } else {
        inWindow = (m >= windowStart && m < shiftEnd + 60)
      }
      const hasDraft = !!localStorage.getItem('fom_daily_posting_next')
      const visible = inWindow || state.activeTab === 'next' || hasDraft
      if (visible !== state.nextTabVisible) set({ nextTabVisible: visible })
    },

    setDate:   (date)    => update(d => ({ ...d, date })),
    setShift: (shift) => update(d => ({
      ...d,
      shift,
      supervisors: d.supervisors.map(s => ({ ...s, time: supShiftTime(shift) })),
      officers:    d.officers.map(o => ({ ...o, time: ofcShiftTime(shift) })),
    })),
    setNotes:  (notes)   => update(d => ({ ...d, notes })),
    setWeather:(weather) => update(d => ({ ...d, weather })),

    addSupervisor: () => update(d => ({ ...d, supervisors: [...d.supervisors, makeEntry()] })),
    addOfficer:    () => update(d => ({ ...d, officers:    [...d.officers,    makeEntry()] })),

    updateSup: (i, patch) => update(d => ({
      ...d, supervisors: d.supervisors.map((s, idx) => idx === i ? { ...s, ...patch } : s)
    })),
    updateOfc: (i, patch) => update(d => ({
      ...d, officers: d.officers.map((o, idx) => idx === i ? { ...o, ...patch } : o)
    })),

    removeSup: (i) => update(d => ({ ...d, supervisors: d.supervisors.filter((_, idx) => idx !== i) })),
    removeOfc: (i) => update(d => ({ ...d, officers:    d.officers.filter((_, idx) => idx !== i) })),

    addPost: (type, i) => update(d => {
      const entries = arr(type, d).map((e, idx) => idx === i
        ? { ...e, posts: [...e.posts, { post: 'Vehicle', from: '', to: '' }] } : e)
      return setArr(type, d, entries)
    }),

    removePost: (type, i, pi) => update(d => {
      const entries = arr(type, d).map((e, idx) => idx === i
        ? { ...e, posts: e.posts.filter((_, j) => j !== pi) } : e)
      return setArr(type, d, entries)
    }),

    updatePost: (type, i, pi, field, val) => update(d => {
      const entries = arr(type, d).map((e, idx) => idx === i
        ? { ...e, posts: e.posts.map((p, j) => j === pi ? { ...p, [field]: val } : p) } : e)
      return setArr(type, d, entries)
    }),

    addTask: (type, i) => update(d => {
      const entries = arr(type, d).map((e, idx) => idx === i
        ? { ...e, tasks: [...e.tasks, { taskId: '', time: '' }] } : e)
      return setArr(type, d, entries)
    }),

    removeTask: (type, i, ti) => update(d => {
      const entries = arr(type, d).map((e, idx) => idx === i
        ? { ...e, tasks: e.tasks.filter((_, j) => j !== ti) } : e)
      return setArr(type, d, entries)
    }),

    updateTask: (type, i, ti, field, val) => update(d => {
      const entries = arr(type, d).map((e, idx) => {
        if (idx !== i) return e
        return {
          ...e,
          tasks: e.tasks.map((t, j) => {
            if (j !== ti) return t
            const updated = { ...t, [field]: val }
            // When the TASK is picked, auto-fill the time from the shift +
            // day-of-week rules — but only if the time isn't already set, so a
            // manual time the user typed is never clobbered. The field stays
            // editable afterward.
            if (field === 'taskId' && !t.time) {
              const auto = secondaryTaskTime(val, d.shift)
              if (auto) updated.time = auto
            }
            return updated
          }),
        }
      })
      return setArr(type, d, entries)
    }),

    updateBreak: (type, i, field, val) => update(d => {
      const entries = arr(type, d).map((e, idx) => {
        if (idx !== i) return e
        const brk: Break = { ...e.brk, [field]: val }
        // Auto-populate end time when start time is edited, unless the user
        // already typed an explicit end time.
        if (field === 'time') {
          const autoEnd = calcBreakEnd(val)
          if (autoEnd) brk.endTime = autoEnd
        }
        return { ...e, brk }
      })
      return setArr(type, d, entries)
    }),

    pickCS: (type, i, cs, shift) => update(d => {
      const time = type === 'sup' ? supShiftTime(shift) : ofcShiftTime(shift)
      const roster = loadRoster()
      const found = roster.find(r => (r.callSign && r.callSign === cs) || (r.name && r.name === cs))
      const name = found?.name ?? cs ?? ''
      const resolvedCS = found?.callSign ?? cs
      const entries = arr(type, d).map((e, idx) => idx === i ? { ...e, cs: resolvedCS, name, time } : e)
      return setArr(type, d, entries)
    }),

    hydrateFromStorage: () => {
      const freshRaw = load<Partial<PostingData> | null>('fom_daily_posting', null)
      let fresh = normalizeData(freshRaw, blankData())
      const today = getToday()
      // If saved posting is from a previous date AND has no personnel, start fresh.
      // But if it has personnel (e.g. promoted next-shift posting), preserve it
      // even if the shift label doesn't match current ET time.
      const hasPersonnel = (
        (fresh.supervisors ?? []).some(s => s.cs) ||
        (fresh.officers ?? []).some(o => o.cs)
      )
      if (fresh.date !== today && !hasPersonnel) {
        fresh = blankData()
        saveCurrent(fresh)
      }
      // Only auto-correct the shift if the posting has NO personnel.
      // If personnel are filled in (from promote or mobile push), preserve
      // the shift as set — don't silently overwrite it.
      if (!hasPersonnel && fresh.date === today) {
        const detected = detectShift()
        if (fresh.shift !== detected) {
          fresh = {
            ...fresh,
            shift: detected,
            supervisors: fresh.supervisors.map(s => ({ ...s, time: supShiftTime(detected) })),
            officers:    fresh.officers.map(o => ({ ...o, time: ofcShiftTime(detected) })),
          }
          saveCurrent(fresh)
        }
      }
      const freshNextRaw = load<Partial<PostingData> | null>('fom_daily_posting_next', null)
      const freshNext = freshNextRaw ? normalizeData(freshNextRaw, emptyNext()) : emptyNext()
      set({ data: fresh, nextData: freshNext, nextTabVisible: !!freshNextRaw || get().nextTabVisible })
    },

    clearForm: () => {
      const state = get()
      if (state.activeTab === 'next') {
        const blank = emptyNext()
        try { localStorage.removeItem('fom_daily_posting_next') } catch { /* silent */ }
        set({ nextData: blank, nextTabVisible: false })
      } else {
        const blank = blankData()
        saveCurrent(blank)
        set({ data: blank })
      }
    },
  }
})

// Re-hydrate when the posting blob changes externally (desktop notes edit
// or cloud pull). Guarded by string-equality check to avoid render thrash.
if (typeof window !== 'undefined') {
  const refresh = () => {
    // Don't overwrite if user edited in last 30 seconds
    const lastEdit = parseInt(localStorage.getItem('fom_posting_last_edit') || '0')
    if (Date.now() - lastEdit < 10_000) return // 10-second guard — don't override active typing
    try {
      const rawCurrent = localStorage.getItem('fom_daily_posting')
      const parsedCurrent = rawCurrent ? JSON.parse(rawCurrent) as Partial<PostingData> : null
      const fresh = parsedCurrent ? normalizeData(parsedCurrent, blankData()) : null

      const rawNext = localStorage.getItem('fom_daily_posting_next')
      const parsedNext = rawNext ? JSON.parse(rawNext) as Partial<PostingData> : null
      const freshNext = parsedNext ? normalizeData(parsedNext, emptyNext()) : null

      const current = usePostingStore.getState()
      const patches: Partial<PostingState> = {}
      if (fresh && JSON.stringify(fresh) !== JSON.stringify(current.data)) {
        patches.data = fresh
      }
      if (freshNext && JSON.stringify(freshNext) !== JSON.stringify(current.nextData)) {
        patches.nextData = freshNext
        patches.nextTabVisible = true
      }
      if (Object.keys(patches).length > 0) usePostingStore.setState(patches)
    } catch { /* silent */ }
  }
  window.addEventListener('castellan:posting-changed', refresh)
  window.addEventListener('castellan:pull-complete', refresh)
  // castellan:roster-updated is intentionally NOT listened to here
  // — roster saves must not reset the posting form
}
